<template>
  <b-form-group class="accordion" role="tablist">
    <b-card no-body :class="borderClass">
      <b-card-header header-tag="header" role="tab">
        <div style="cursor: pointer" @click="visible = !visible">TARGET</div>
      </b-card-header>
      <b-collapse v-model="visible" role="tabpanel">
        <b-card-body>
          <VForm ref="observer" v-slot="{ meta: formMeta }" as="div">
            <b-form @submit.prevent="saveForm">
              <b-input-group class="justify-between">
                <label
                  v-b-tooltip.hover="
                    'PK/PD target type: AUC₂₄ (area under the curve), peak/trough concentrations, or fT>MIC (time above minimum inhibitory concentration)'
                  "
                  >Method</label
                >
                <b-form-select
                  id="target"
                  ref="pdtMethod"
                  v-model="form.target"
                  placeholder="Select method"
                  class="unit-dropdown"
                  value-field="value"
                  text-field="text"
                  :options="targetList"
                  :clearable="false"
                  @update:model-value="updateTarget"
                ></b-form-select>
                <b-form-select
                  id="pdtUnit"
                  ref="pdtUnit"
                  v-model="form.pdtUnit"
                  class="unit-dropdown"
                  value-field="value"
                  text-field="text"
                  :options="targetUnits"
                  :clearable="false"
                  :disabled="true"
                  required
                  @update:model-value="saveForm"
                >
                </b-form-select>
              </b-input-group>
              <b-input-group>
                <label
                  v-b-tooltip.hover="
                    'Desired therapeutic range — doses will be recommended to achieve a value between min and max'
                  "
                  >Range</label
                >
                <VField v-slot="{ meta }" v-model="form.pdtMin" name="pdtMin" :rules="targetRules">
                  <b-form-input
                    id="pdt"
                    ref="pdtMin"
                    v-model="form.pdtMin"
                    type="number"
                    :number="true"
                    class="input-with-unit"
                    placeholder="Min value"
                    :state="getValidationState(meta)"
                    required
                    @input="saveForm"
                  />
                </VField>
                <span class="px-1">to</span>
                <VField v-slot="{ meta }" v-model="form.pdtMax" name="pdtMax" :rules="targetRules">
                  <b-form-input
                    id="pdtMax"
                    ref="pdtMax"
                    v-model="form.pdtMax"
                    type="number"
                    :number="true"
                    class="input-with-unit"
                    placeholder="Max value"
                    :state="getValidationState(meta)"
                    required
                    @input="saveForm"
                  />
                </VField>
              </b-input-group>
              <VErrorMessage name="pdtMin" as="div" class="invalid-feedback d-block" />
              <VErrorMessage name="pdtMax" as="div" class="invalid-feedback d-block" />
              <hr />
              <span class="flex align-content-center">
                <b-button
                  class="rounded-circle shadow bg-secondary"
                  title="Add target"
                  :disabled="!formMeta.valid || !isFormValid"
                  @click="saveForm"
                  ><PlusIcon></PlusIcon
                ></b-button>
              </span>
            </b-form>
          </VForm>
        </b-card-body>
      </b-collapse>
    </b-card>
  </b-form-group>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { displaySIUnits, targets, units } from '@/models/utils/constants.js';
import PlusIcon from 'vue-material-design-icons/Plus.vue';
import { DrugTargetNames } from '@/models/utils/constants';
import { useDosingStore } from '@/stores/dosing';

defineOptions({ name: 'TargetInput' });

const props = defineProps<{ targetData?: any; drug?: any }>();

const store = useDosingStore();
const visible = ref(false);
const observer = ref<any>(null);

const targetUnits = units.targetConcentration.map((u: string) => ({
  value: u,
  text: displaySIUnits(u),
}));

function initForm() {
  const target = props.targetData ?? null;
  return {
    target: target
      ? target.target
      : props.drug
        ? props.drug.target[0].type
        : (null as string | null),
    pdtMin: target
      ? target.pdtMin
      : props.drug
        ? props.drug.target[0].min
        : (null as number | null),
    pdtMax: target
      ? target.pdtMax
      : props.drug
        ? props.drug.target[0].max
        : (null as number | null),
    pdtUnit: target
      ? target.pdtUnit
      : props.drug
        ? props.drug.target[0].unit
        : (null as string | null),
  };
}

const form = ref(initForm());
const targetList = ref(getTargetList());

watch(
  () => props.targetData,
  () => {
    form.value = initForm();
  },
);
watch(
  () => props.drug,
  () => {
    targetList.value = getTargetList();
  },
);

const targetRules = computed(() => ({
  required: true,
  targetBetween: {
    method: form.value.target,
    unit: form.value.pdtUnit,
    minValue: form.value.pdtMin,
    maxValue: form.value.pdtMax,
    drug: props.drug,
  },
}));

const borderClass = computed(() => {
  const currentTarget = store.getTarget;
  if (
    !currentTarget?.target ||
    currentTarget.pdtMin == null ||
    currentTarget.pdtMax == null ||
    !currentTarget.pdtUnit
  ) {
    return 'mb-1 border-danger';
  }
  return 'mb-1';
});

const isFormValid = computed(() => form.value.pdtMin !== null && form.value.pdtMax !== null);

function getTargetList() {
  return props.drug
    ? props.drug.target.map((t: any) => ({ value: t.type, text: DrugTargetNames[t.type] }))
    : targets;
}

function updateTarget() {
  if (props.drug && form.value.target) {
    const target = props.drug.target.find((t: any) => t.type === form.value.target);
    if (target) {
      form.value.pdtMin = target.min;
      form.value.pdtMax = target.max;
      form.value.pdtUnit = target.unit;
      saveForm();
    }
  }
}

function saveForm() {
  observer.value.validate().then(({ valid }: { valid: boolean }) => {
    if (valid) store.saveTarget(form.value);
  });
}

function getValidationState(meta: { validated: boolean; valid: boolean }): boolean | null {
  return meta.validated ? meta.valid : null;
}
</script>

<style scoped>
span > .unit-dropdown {
  margin: 0;
}
</style>
